import * as THREE from 'three';
import { easeInOutCubic } from '../animation/easings';
import type { ResponsiveValues } from '../../types';
import { useAppStore } from '../../store/useAppStore';
import { textureCache } from '../../hooks/media/useMediaLoader';

interface SpiralProps {
  spiralX: number;
  spiralY: number;
  spiralZ: number;
  spiralRotY: number;
  spiralScale: number;
}

export const calculateSpiralPosition = (
  _index: number,
  angle: number,
  values: ResponsiveValues
): [number, number, number] => {
  const radius = values.radius;
  const heightLoop = values.heightLoop;

  const spiralX = Math.sin(angle * 2) * radius;
  const spiralZ = Math.cos(angle * 2) * radius;
  const spiralY =
    (((angle / (Math.PI * 2)) * heightLoop) % heightLoop) - heightLoop / 2;

  return [spiralX, spiralY, spiralZ];
};

export const updateItemPosition = (
  item: THREE.Object3D,
  index: number,
  selectedIndex: number | null,
  spiralProps: SpiralProps,
  transitionProgress: number,
  values: ResponsiveValues,
  viewport: { width: number; height: number; factor: number },
  url: string
) => {
  const t = easeInOutCubic(transitionProgress);
  const { spiralX, spiralY, spiralZ, spiralRotY, spiralScale } = spiralProps;
  const spiralPos = new THREE.Vector3(spiralX, spiralY, spiralZ);

  const targetLeft = 210;
  const targetTop = 215;
  const targetLeftInWorld = (targetLeft / window.innerWidth) * viewport.width;
  const targetTopInWorld = (targetTop / window.innerHeight) * viewport.height;

  if (selectedIndex === index) {
    const focusedPos = new THREE.Vector3(
      window.innerWidth < 768 ? 0 : -viewport.width / 2 + targetLeftInWorld,
      viewport.height / 2 -
        targetTopInWorld +
        (window.innerWidth < 768 ? 0.5 : 0)
    );

    const texture = textureCache.get(url);
    const isPortrait = texture?.image.height > texture?.image.width;
    if (isPortrait && window.innerWidth >= 768) {
      focusedPos.x -= 2.3;
    }

    const scrollOffset = useAppStore.getState().galleryScrollOffset;
    const isMobile = window.innerWidth < 768;
    const viewportFactor = isMobile
      ? viewport.height / window.innerHeight
      : viewport.width / window.innerWidth;
    const worldOffset = scrollOffset * viewportFactor;

    if (isMobile) {
      focusedPos.y += worldOffset;
    } else {
      focusedPos.x -= worldOffset;
    }

    item.position.lerpVectors(spiralPos, focusedPos, t);

    // Rotation fixe vers l'avant au lieu de regarder la caméra
    const currentRotation = THREE.MathUtils.euclideanModulo(
      item.rotation.y,
      Math.PI * 2
    );
    const targetRotation = Math.PI; // Rotation de 180° pour regarder vers z positif

    // Trouver le chemin le plus court
    let adjustedTargetRotation = targetRotation;
    if (Math.abs(adjustedTargetRotation - currentRotation) > Math.PI) {
      if (adjustedTargetRotation > currentRotation) {
        adjustedTargetRotation -= Math.PI * 2;
      } else {
        adjustedTargetRotation += Math.PI * 2;
      }
    }

    item.rotation.y = THREE.MathUtils.lerp(
      currentRotation,
      adjustedTargetRotation,
      t
    );

    // Force une taille absolue plus grande
    const baseScale = values.imageScale[1];

    // Sur desktop, l'image doit faire 150px de hauteur
    if (window.innerWidth > 525) {
      // Convertir 150px en unités Three.js
      const targetHeightInPixels = 200;
      const targetHeightInWorld =
        (targetHeightInPixels / window.innerHeight) * viewport.height;
      const scale = targetHeightInWorld / baseScale;
      item.scale.setScalar(scale);
    } else {
      item.scale.setScalar((4 / baseScale) * (viewport.width / 8.5));
    }
  } else if (selectedIndex !== null) {
    const angle = (index * Math.PI * 2) / values.projectsPerLoop;
    const radius = 15;
    const dispersePos = new THREE.Vector3(
      Math.cos(angle) * radius,
      Math.sin(angle) * radius,
      15
    );

    item.position.lerpVectors(spiralPos, dispersePos, t);
    item.rotation.y = spiralRotY;

    const targetScale = THREE.MathUtils.lerp(spiralScale, spiralScale * 0.8, t);
    item.scale.setScalar(targetScale);
  } else {
    const currentPos = item.position.clone();
    item.position.lerpVectors(currentPos, spiralPos, 1 - t);

    // Normaliser la rotation de retour
    const currentRotation = THREE.MathUtils.euclideanModulo(
      item.rotation.y,
      Math.PI * 2
    );
    let targetRotation = THREE.MathUtils.euclideanModulo(
      spiralRotY,
      Math.PI * 2
    );

    if (Math.abs(targetRotation - currentRotation) > Math.PI) {
      if (targetRotation > currentRotation) {
        targetRotation -= Math.PI * 2;
      } else {
        targetRotation += Math.PI * 2;
      }
    }

    item.rotation.y = THREE.MathUtils.lerp(
      currentRotation,
      targetRotation,
      1 - t
    );

    const currentScale = item.scale.x;
    const targetScale = spiralScale;
    item.scale.setScalar(
      THREE.MathUtils.lerp(currentScale, targetScale, 1 - t)
    );
  }
};
